import { ContainerModule } from 'inversify';
import { useEffect } from 'react';
import { GenericObject } from '@10d/tend-ui/types';
import ApiBufferFactory from 'api/apiServices/ApiBufferFactory';
import ApiBufferService from 'api/apiServices/ApiBufferService';
import {
  IApiBufferFactory,
  IPaginationRequestOptions,
  TApiBufferConstructor,
} from 'api/apiServices/types';
import { EGlobalDIKeys } from 'core/di/keys';
import ApiLoadingStateService from 'services/ApiLoadingStateService';
import LoggerService from 'services/logger/LoggerService';
import { ILoggerService } from 'services/logger/types';
import { globalDIContainer } from 'core/di';
import { TModalModelContructor } from 'models/Modal/types';
import ModalModel from 'models/Modal/ModalModel';
import {
  IDynamicList,
  IList,
  TDynamicListLoader,
  TListArrayModelConstructor,
  TListMapModelConstructor,
} from 'models/List/types';
import ModalModelFactoryService from 'services/modal/ModalModelFactoryService';
import { IModalModelFactoryService } from 'services/modal/types';
import { TSelectionModelConstructor } from 'models/Selection/types';
import SelectionModel from 'models/Selection/SelectionModel';
import { IListArrayFactoryService, IListMapFactoryService } from 'services/list/types';
import ListArrayFactoryService from 'services/list/ListArrayFactoryService';
import { ISelectionModelFactoryService } from 'services/selection/types';
import SelectionModelFactoryService from 'services/selection/SelectionModelFactoryService';
import ListMapModel from 'models/List/ListMapModel';
import ListMapFactoryService from 'services/list/ListMapFactoryService';
import ModelSettingsManager from 'services/settingsManager/ModelSettingsManager';
import ModelSettingsManagerFactory from 'services/settingsManager/ModelSettingsManagerFactory';
import CriteriaTypesStore from 'stores/CriteriaTypes/CriteriaTypes';
import CriteriaUnitsStore from 'stores/CriteriaUnits/CriteriaUnits';
import WebSocketService from 'api/apiServices/ws/WebSocketService';
import Channels from 'api/apiServices/ws/Channels';
import AWSIApiService from 'api/apiServices/aws/AWSApiService';
import ListArrayModel from 'models/List/ListArrayModel';
import AnalyticsLoggerService from 'services/logger/AnalyticsLoggerService';
import { IAnalyticsLoggerController } from 'controllers/loggers/AnalitycsLoggerController/types';
import AnalitycsLoggerController from 'controllers/loggers/AnalitycsLoggerController';
import {
  IAnalyticsLoggerCaseRegistrator,
  IAnalyticsLoggerService,
} from 'services/logger/AnalyticsLoggerService/types';
import AnalyticsLoggerCaseRegistrator from 'services/logger/AnalyticsLoggerService/AnalyticsLoggerCaseRegistrator';
import DynamicList from 'models/List/DynamicList';
import { IRegionsObserver } from 'controllers/regionsObserver/types';
import RegionsObserver from 'controllers/regionsObserver';
import HistoryStack from 'models/HistoryStack/HistoryStack';
import { IAsyncListControllerSettings } from 'controllers/asyncList/types';
import AsyncListController from 'controllers/asyncList/AsyncList';
import {
  ISubscription,
  ISubscriptionSettings,
} from '../api/apiServices/ws/Subscription/types';
import Subscription from '../api/apiServices/ws/Subscription';
import RPCService from '../api/apiServices/ws/rpc/RPCService';
import RPCMessage from '../api/apiServices/ws/rpc/RPCMessage';
import { IChannel } from '../api/apiServices/ws/Channel/types';
import { WS_MESSAGE_TYPE } from '../api/websockets';
import RPCMessages from '../api/apiServices/ws/rpc/RPCMessages';
import RPCApi from '../api/apiServices/ws/rpc/RPCApi';
import { IRPCMessage } from '../api/apiServices/ws/rpc/types';

export const appModule = new ContainerModule(({ bind }) => {
  bind<TApiBufferConstructor<object>>(
    EGlobalDIKeys.ApiBufferServiceConstructor,
  ).toConstantValue(ApiBufferService);
  bind<TModalModelContructor>(EGlobalDIKeys.ModalModelConstructor).toConstantValue(
    ModalModel,
  );
  bind<TListArrayModelConstructor>(
    EGlobalDIKeys.ListArrayModelConstructor,
  ).toConstantValue(ListArrayModel);
  bind<TListMapModelConstructor>(EGlobalDIKeys.ListMapModelConstructor).toConstantValue(
    ListMapModel,
  );
  bind<IListMapFactoryService>(EGlobalDIKeys.ListMapModelFactoryService).to(
    ListMapFactoryService,
  );
  bind<IRegionsObserver>(EGlobalDIKeys.RegionsObserver)
    .to(RegionsObserver)
    .inSingletonScope();
  bind<TSelectionModelConstructor>(
    EGlobalDIKeys.SelectionModelConstructor,
  ).toConstantValue(SelectionModel);

  bind<IModalModelFactoryService>(EGlobalDIKeys.ModalModelFactoryService).to(
    ModalModelFactoryService,
  );
  bind<IListArrayFactoryService>(EGlobalDIKeys.ListArrayModelFactoryService).to(
    ListArrayFactoryService,
  );
  bind<ISelectionModelFactoryService>(EGlobalDIKeys.SelectionModelFactoryService).to(
    SelectionModelFactoryService,
  );

  bind<
    <T, List extends IList<T>>(
      list: List,
      loader: TDynamicListLoader<T>,
    ) => IDynamicList<T>
  >(EGlobalDIKeys.DynamicListFactory).toFactory(() => {
    return <T, List extends IList<T>>(list: List, loader: TDynamicListLoader<T>) => {
      return new DynamicList<T, List>(list, loader);
    };
  });

  bind<IApiBufferFactory>(EGlobalDIKeys.ApiBufferServiceFactory).to(ApiBufferFactory);

  bind<ILoggerService>(EGlobalDIKeys.LoggerService).to(LoggerService).inSingletonScope();
  bind<IAnalyticsLoggerService>(EGlobalDIKeys.AnalyticsLoggerService)
    .to(AnalyticsLoggerService)
    .inSingletonScope();
  bind<IAnalyticsLoggerController>(EGlobalDIKeys.AnalyticsLogger)
    .to(AnalitycsLoggerController)
    .inSingletonScope();
  bind<IAnalyticsLoggerCaseRegistrator>(EGlobalDIKeys.AnalyticsCasesRegistrator)
    .to(AnalyticsLoggerCaseRegistrator)
    .inSingletonScope();

  bind(EGlobalDIKeys.ApiLoadingStateService)
    .to(ApiLoadingStateService)
    .inSingletonScope();

  bind(EGlobalDIKeys.SettingsManagerServiceConstructor).toConstantValue(
    ModelSettingsManager,
  );
  bind(EGlobalDIKeys.SettingsManagerServiceFactory)
    .to(ModelSettingsManagerFactory)
    .inSingletonScope();

  bind(EGlobalDIKeys.CriteriaTypesStore).to(CriteriaTypesStore).inSingletonScope();
  bind(EGlobalDIKeys.CriteriaUnitsStore).to(CriteriaUnitsStore).inSingletonScope();

  bind(EGlobalDIKeys.WebSocketService).to(WebSocketService).inSingletonScope();

  bind<(settings: ISubscriptionSettings) => ISubscription>(
    EGlobalDIKeys.Subscription,
  ).toFactory(
    context => (settings: ISubscriptionSettings) =>
      new Subscription(settings, context.get(EGlobalDIKeys.WebSocketService)),
  );
  bind(EGlobalDIKeys.Channels).to(Channels).inSingletonScope();

  bind(EGlobalDIKeys.AWSApiService).to(AWSIApiService);
  bind(EGlobalDIKeys.HistoryStack).to(HistoryStack).inSingletonScope();

  bind(EGlobalDIKeys.RPCService).to(RPCService).inSingletonScope();
  bind<<T>(channel: IChannel, messageType: WS_MESSAGE_TYPE, meta: T) => IRPCMessage<T>>(
    EGlobalDIKeys.RPCMessageFactory,
  ).toFactory(() => {
    return <T>(channel: IChannel, messageType: WS_MESSAGE_TYPE, meta: T) =>
      new RPCMessage(channel, messageType, meta);
  });
  bind(EGlobalDIKeys.RPCMessages).to(RPCMessages).inSingletonScope();
  bind(EGlobalDIKeys.RPCApi).to(RPCApi).inSingletonScope();

  bind<
    <Item extends GenericObject, Filter extends IPaginationRequestOptions>(
      settings: IAsyncListControllerSettings<Item, Filter>,
    ) => AsyncListController<Item, Filter>
  >(EGlobalDIKeys.AsyncListControllerFactory).toFactory(
    context =>
      <Item extends GenericObject, Filter extends IPaginationRequestOptions>(
        settings: IAsyncListControllerSettings<Item, Filter>,
      ) =>
        new AsyncListController(
          context.get(EGlobalDIKeys.ListMapModelFactoryService),
          settings,
        ),
  );
});

export const useInitApp = () => {
  useEffect(() => {
    globalDIContainer.load(appModule);

    return () => {
      globalDIContainer.unload(appModule);
    };
  }, []);
};
